import React, { useState } from 'react'
import { Autocomplete, Box, Button, IconButton, Popover, Popper, Typography } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { StyledTextField } from '../../../ui/StyledComponents'

// Como mucho se dibujan estas opciones: en N° Documento casi todos los valores son únicos
const MAX_OPTIONS = 50

const iconActiveSx = { color: '#1976d2', backgroundColor: 'rgba(25, 118, 210, 0.12)', p: '2px', ml: 0.5 }
const iconSx = { color: '#9e9e9e', p: '2px', ml: 0.5 }
const iconFontSx = { fontSize: 16 }
const anchorOrigin = { vertical: 'bottom', horizontal: 'left' }
const contentSx = { p: 1.5, width: 300 }
const actionsSx = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mt: 1, minHeight: 30 }
const listboxProps = { style: { maxHeight: 240 } }

export const emptyLabel = '(Vacío)'

const optionLabel = (option) => (typeof option === 'string' ? option : option.value || emptyLabel)

const filterGroups = (options, { inputValue }) => {
    const term = inputValue.trim().toLowerCase()
    const matches = term ? options.filter((o) => optionLabel(o).toLowerCase().includes(term)) : options
    return matches.slice(0, MAX_OPTIONS)
}

// La lista va siempre debajo del campo: sin flip no se da vuelta y no tapa lo que se escribe
const flipDisabled = [{ name: 'flip', enabled: false }]
const BottomPopper = (props) => <Popper {...props} placement="bottom-start" modifiers={flipDisabled} />

// El popper se dibuja dentro del popover (disablePortal): el papel no lo recorta
const paperProps = { sx: { overflow: 'visible' } }

// Popover con el buscador de valores: se monta solo mientras está abierto. La lista se despliega al escribir o
// con la flecha, y elegir un valor no cierra el popover: se cierra al hacer clic afuera o con Escape
function ColumnFilterPopover({ anchorEl, label, groups, filtro, onChange, onClose }) {
    const [inputValue, setInputValue] = useState(filtro ? optionLabel(filtro.exact ? filtro : filtro.value) : '')

    const value = filtro?.exact
        ? groups.find((g) => g.value === filtro.value) ?? { value: filtro.value, count: 0 }
        : null

    // Elegir una opción filtra por ese valor exacto; Enter con texto libre filtra por "contiene"
    const handleChange = (_, newValue) => {
        if (newValue === null) onChange(null)
        else if (typeof newValue === 'string') onChange(newValue.trim() ? { value: newValue.trim(), exact: false } : null)
        else onChange({ value: newValue.value, exact: true })
    }

    const handleQuitar = () => {
        setInputValue('')
        onChange(null)
    }

    return (
        <Popover open anchorEl={anchorEl} onClose={onClose} anchorOrigin={anchorOrigin} PaperProps={paperProps}>
            <Box sx={contentSx}>
                <Autocomplete
                    size="small"
                    freeSolo
                    forcePopupIcon
                    disablePortal
                    PopperComponent={BottomPopper}
                    options={groups}
                    value={value}
                    inputValue={inputValue}
                    onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                    onChange={handleChange}
                    isOptionEqualToValue={(a, b) => a.value === b.value}
                    getOptionLabel={optionLabel}
                    filterOptions={filterGroups}
                    renderOption={(props, option) => (
                        <li {...props} key={option.value}>
                            {optionLabel(option)} ({option.count})
                        </li>
                    )}
                    ListboxProps={listboxProps}
                    noOptionsText="Sin coincidencias. Enter filtra por lo escrito."
                    renderInput={(params) => (
                        <StyledTextField {...params} autoFocus label={`Buscar ${label.toLowerCase()}`} />
                    )}
                />
                <Box sx={actionsSx}>
                    <Typography variant="caption" color="text.secondary">
                        Elegí un valor o escribí y apretá Enter
                    </Typography>
                    {filtro && <Button size="small" onClick={handleQuitar}>Quitar filtro</Button>}
                </Box>
            </Box>
        </Popover>
    )
}

// Lupa del encabezado. `groups` son los valores de la columna con su cantidad: [{ value, count }].
// `filtro` es { value, exact } o null
export default function ColumnFilter({ label, groups, filtro, onChange }) {
    const [anchorEl, setAnchorEl] = useState(null)
    const title = filtro ? `Filtrado por "${filtro.value || emptyLabel}"` : `Filtrar ${label.toLowerCase()}`

    return (
        <>
            <IconButton
                size="small"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                title={title}
                aria-label={title}
                sx={filtro ? iconActiveSx : iconSx}
            >
                <SearchIcon sx={iconFontSx} />
            </IconButton>
            {anchorEl && (
                <ColumnFilterPopover
                    anchorEl={anchorEl}
                    label={label}
                    groups={groups}
                    filtro={filtro}
                    onChange={onChange}
                    onClose={() => setAnchorEl(null)}
                />
            )}
        </>
    )
}
